import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { Box, ButtonBase, CircularProgress, InputAdornment, TextField, Typography } from '@material-ui/core';
import usePhoneVerify from './usePhoneVerify';

// 휴대폰 문자 인증(팝빌) — 회원가입·아이디/비밀번호 찾기·번호 변경 네 화면이 같은 컴포넌트를 쓴다.
// 로그인 폼 입력창(.formArea)과 같은 틀 안에 [인증번호 받기]·[확인] 버튼과 남은 시간을 넣는다.
const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    '& .MuiInputBase-root': { paddingRight: 8 },
  },
  action: {
    height: 38,
    padding: '0 14px',
    borderRadius: 8,
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: -0.6,
    whiteSpace: 'nowrap',
    transition: 'background-color .12s',
    '&:hover': { backgroundColor: theme.palette.primary.dark },
    '&$disabled': { backgroundColor: '#e4e4e9', color: '#ffffff' },
  },
  disabled: {},
  timer: {
    marginRight: 10,
    fontSize: 14,
    color: theme.palette.primary.dark,
    fontVariantNumeric: 'tabular-nums',
  },
  done: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 38,
    padding: '0 12px',
    borderRadius: 8,
    backgroundColor: '#fef7f5',
    color: theme.palette.primary.dark,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: -0.6,
    '& svg': { marginRight: 4 },
  },
  error: {
    display: 'block',
    marginTop: 2,
    marginBottom: 14,
    paddingLeft: 4,
    color: theme.palette.primary.dark,
  },
}));

const mmss = (sec) => String(Math.floor(sec / 60)).padStart(2, '0') + ':' + String(sec % 60).padStart(2, '0');

function PhoneVerify({ purpose, onVerified, initialPhone = '', className }) {
  const cs = useStyles();
  const v = usePhoneVerify({ purpose, onVerified, initialPhone });
  const verified = v.step === 'verified';
  const onEnter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); fn(); } };

  return (
    <Box className={clsx('mainFormGroup', cs.root, className)}>
      <TextField
        fullWidth
        variant="outlined"
        className="formArea"
        placeholder="휴대폰 번호"
        value={v.phone}
        onChange={(e) => v.changePhone(e.target.value)}
        onKeyDown={onEnter(v.send)}
        disabled={verified}
        inputProps={{ inputMode: 'tel', autoComplete: 'tel', 'aria-label': '휴대폰 번호' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ButtonBase
                className={clsx(cs.action, (v.busy || verified) && cs.disabled)}
                classes={{ disabled: cs.disabled }}
                disabled={v.busy || verified}
                onClick={v.send}
              >
                {v.busy && v.step === 'idle' ? <CircularProgress size={16} color="inherit" /> : v.step === 'idle' ? '인증번호 받기' : '재전송'}
              </ButtonBase>
            </InputAdornment>
          ),
        }}
      />
      {v.step !== 'idle' && (
        <TextField
          fullWidth
          variant="outlined"
          className="formArea"
          placeholder="인증번호 6자리"
          value={v.code}
          onChange={(e) => v.changeCode(e.target.value)}
          onKeyDown={onEnter(v.verify)}
          disabled={verified}
          autoFocus
          inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code', 'aria-label': '인증번호' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {verified ? (
                  <span className={cs.done}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                    인증 완료
                  </span>
                ) : (
                  <>
                    {v.remain > 0 && <span className={cs.timer}>{mmss(v.remain)}</span>}
                    <ButtonBase
                      className={clsx(cs.action, v.busy && cs.disabled)}
                      classes={{ disabled: cs.disabled }}
                      disabled={v.busy}
                      onClick={v.verify}
                    >
                      {v.busy ? <CircularProgress size={16} color="inherit" /> : '확인'}
                    </ButtonBase>
                  </>
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
      {v.error && <Typography variant="caption" className={cs.error}>{v.error}</Typography>}
    </Box>
  );
}

export default PhoneVerify;
